import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SEOHead from '@/components/base/SEOHead';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(article?.likeCount || 0);

  const handleLike = () => {
    if (!liked) {
      setLiked(true);
      setLikeCount((prev) => prev + 1);
    }
  };

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到 - 51每日大赛" description="抱歉，您访问的文章不存在。" />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center bg-background-50">
          <div className="text-center">
            <h1 className="text-3xl font-black text-foreground-900 mb-4">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您访问的文章不存在或已被删除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '51每日大赛',
    },
    url: `http://chiguaji.cn/article/${article.id}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://chiguaji.cn/article/${article.id}`,
    },
    keywords: article.tags.join(','),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://chiguaji.cn/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.category,
        item: `http://chiguaji.cn/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  const combinedSchema = { ...articleSchema, ...breadcrumbSchema };

  return (
    <>
      <SEOHead
        title={`${article.title} - 51每日大赛`}
        description={article.excerpt}
        keywords={`${article.title},${article.category},${article.tags.join(',')},51每日大赛,网红明星塌房`}
        canonicalUrl={`http://chiguaji.cn/article/${article.id}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        articlePublishedTime={article.publishDate}
        articleModifiedTime={article.publishDate}
        articleTags={article.tags}
        schema={combinedSchema}
      />

      <Navbar />

      <main>
        {/* Article Hero */}
        <section className="relative w-full h-[280px] md:h-[400px] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/40"></div>
          <div className="absolute inset-0 flex items-end">
            <div className="w-full px-4 md:px-6 pb-8">
              <div className="max-w-7xl mx-auto">
                <span className="inline-block px-2.5 py-1 bg-primary-500 text-background-50 text-xs rounded-md mb-3 whitespace-nowrap">
                  {article.category}
                </span>
                <h1 className="text-white text-2xl md:text-4xl font-black leading-tight max-w-3xl">
                  {article.title}
                </h1>
              </div>
            </div>
          </div>
        </section>

        {/* Content Area */}
        <section className="w-full bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: article.category, path: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />

            <div className="flex flex-col lg:flex-row gap-8 pb-14">
              {/* Article Content */}
              <article className="flex-1">
                {/* Meta Info */}
                <div className="flex flex-wrap items-center gap-4 mb-8 pb-6 border-b border-background-200">
                  <span className="text-foreground-500 text-sm">
                    <i className="ri-user-line mr-1"></i>{article.author}
                  </span>
                  <span className="text-foreground-500 text-sm">
                    <i className="ri-calendar-line mr-1"></i>{article.publishDate}
                  </span>
                  <span className="text-foreground-500 text-sm">
                    <i className="ri-eye-line mr-1"></i>
                    {article.readCount >= 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount} 阅读
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {article.tags.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 bg-background-100 text-foreground-500 text-xs rounded-full whitespace-nowrap">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Article Body */}
                <div
                  className="prose prose-base max-w-none text-foreground-800 leading-relaxed [&>p]:mb-5 [&>p]:text-base [&>p]:leading-loose"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                {/* Action Bar */}
                <div className="flex flex-wrap items-center gap-3 mt-10 pt-6 border-t border-background-200">
                  <button
                    onClick={handleLike}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      liked
                        ? 'bg-accent-100 text-accent-600 border border-accent-300'
                        : 'bg-background-100 text-foreground-600 hover:bg-accent-50 hover:text-accent-500 border border-background-200'
                    }`}
                  >
                    <i className={`${liked ? 'ri-heart-fill' : 'ri-heart-line'} text-base`}></i>
                    点赞 {likeCount >= 10000 ? `${(likeCount / 10000).toFixed(1)}万` : likeCount}
                  </button>
                  <button className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-100 text-foreground-600 border border-background-200 rounded-full text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-share-forward-line text-base"></i>
                    分享爆料
                  </button>
                  <Link
                    to="/about"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    我要爆料
                    <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
              </article>

              {/* Sidebar */}
              <aside className="lg:w-80 flex-shrink-0">
                {/* Author Card */}
                <div className="bg-background-100 rounded-xl p-5 mb-5">
                  <h3 className="text-foreground-900 font-bold text-base mb-3">关于作者</h3>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-user-line text-primary-500"></i>
                    </div>
                    <div>
                      <p className="text-foreground-900 text-sm font-medium">{article.author}</p>
                      <p className="text-foreground-500 text-xs">51每日大赛编辑</p>
                    </div>
                  </div>
                  <p className="text-foreground-500 text-xs leading-relaxed">
                    专注网红明星塌房事件追踪，第一时间为您带来最新吃瓜资讯。
                  </p>
                </div>

                {/* Hot Tags */}
                <div className="bg-background-100 rounded-xl p-5 mb-5">
                  <h3 className="text-foreground-900 font-bold text-base mb-3">热门标签</h3>
                  <div className="flex flex-wrap gap-2">
                    {['网红翻车', '明星塌房', '娱乐圈', '吃瓜', '八卦', '黑料', '爆料', '人设崩塌'].map((tag) => (
                      <Link
                        key={tag}
                        to={`/category/meiri-chigua`}
                        className="px-3 py-1.5 bg-background-50 text-foreground-600 text-xs rounded-full hover:bg-primary-100 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {tag}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Reading Stats */}
                <div className="bg-background-100 rounded-xl p-5">
                  <h3 className="text-foreground-900 font-bold text-base mb-4">文章数据</h3>
                  <div className="flex items-center justify-between">
                    <div className="text-center flex-1">
                      <p className="text-2xl font-black text-foreground-900">
                        {article.readCount >= 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
                      </p>
                      <p className="text-foreground-500 text-xs mt-1">阅读量</p>
                    </div>
                    <div className="w-px h-10 bg-background-200"></div>
                    <div className="text-center flex-1">
                      <p className="text-2xl font-black text-foreground-900">
                        {likeCount >= 10000 ? `${(likeCount / 10000).toFixed(1)}万` : likeCount}
                      </p>
                      <p className="text-foreground-500 text-xs mt-1">点赞数</p>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-black text-foreground-900 mb-8">
                相关爆料推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}